import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiCall } from '../AuthContext';
import {
  ArrowLeft, Search, Printer, ShoppingBag, Store, Loader2, Package,
  CheckCircle2, AlertCircle, X, Trash2,
} from 'lucide-react';
import {
  quickPrintProduct, quickPushShopifyProduct, quickPushSquareProduct,
  openSingleLabelPrint,
} from '../utils/productActions';
import BarcodeScannerButton from '../components/BarcodeScannerButton';

export default function BatchDetail() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [rowAction, setRowAction] = useState({}); // {product_id: 'print'|'shopify'|'square'}
  const [rowStatus, setRowStatus] = useState({}); // {product_id: {ok:true}|{ok:false,reason:'..'}}
  const [labelTemplates, setLabelTemplates] = useState([]);
  const [printTemplateId, setPrintTemplateId] = useState('');
  const [labelPreview, setLabelPreview] = useState(null); // {image, product_name, size}
  const [error, setError] = useState('');
  const searchInputRef = useRef(null);

  // Debounce search input → fires the filter request
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Auto-focus the search bar so a USB barcode scanner immediately filters the list
  useEffect(() => {
    setTimeout(() => { searchInputRef.current?.focus(); }, 150);
  }, []);

  const fetchBatch = useCallback(async () => {
    try {
      const res = await apiCall('/api/batches', { method: 'GET' });
      const b = (res.data?.batches || []).find(x => x.batch_id === batchId);
      setBatch(b || null);
    } catch (_e) { /* noop */ }
  }, [batchId]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('batch_id', batchId);
      if (search) params.append('search', search);
      const res = await apiCall(`/api/products?${params.toString()}`, { method: 'GET' });
      setProducts(res.data?.products || []);
    } catch (_e) { /* noop */ } finally { setLoading(false); }
  }, [batchId, search]);

  useEffect(() => { fetchBatch(); }, [fetchBatch]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    apiCall('/api/label-templates', { method: 'GET' }).then(r => {
      const list = r.data || [];
      setLabelTemplates(list);
      const two = list.find(t => (t.size || '').toLowerCase() === '2x1');
      if (two) setPrintTemplateId(two.id);
      else if (list[0]) setPrintTemplateId(list[0].id);
    }).catch(() => {});
  }, []);

  const handleQuickPrint = async (p) => {
    setError('');
    if (!printTemplateId) { setError('Select a label template first.'); return; }
    setRowAction(prev => ({ ...prev, [p.product_id]: 'print' }));
    const res = await quickPrintProduct(p, printTemplateId);
    setRowAction(prev => { const n = { ...prev }; delete n[p.product_id]; return n; });
    if (!res.ok) {
      setRowStatus(prev => ({ ...prev, [p.product_id]: { ok: false, reason: res.reason } }));
      setError(res.reason);
      return;
    }
    const tpl = labelTemplates.find(t => t.id === printTemplateId);
    const size = tpl?.size || '2x1';
    setLabelPreview({ image: res.image, product_name: p.product_name, size });
    setRowStatus(prev => ({ ...prev, [p.product_id]: { ok: true } }));
  };

  const handleQuickPushShopify = async (p) => {
    setError('');
    setRowAction(prev => ({ ...prev, [p.product_id]: 'shopify' }));
    const res = await quickPushShopifyProduct(p);
    setRowAction(prev => { const n = { ...prev }; delete n[p.product_id]; return n; });
    setRowStatus(prev => ({ ...prev, [p.product_id]: res.ok ? { ok: true } : { ok: false, reason: res.reason } }));
    if (!res.ok) {
      if (res.needsReconnect) {
        setError('Shopify missing read_publications scope. Reconnect at Configuration → Shopify.');
      } else {
        setError(res.reason);
      }
    }
  };

  const handleQuickPushSquare = async (p) => {
    setError('');
    setRowAction(prev => ({ ...prev, [p.product_id]: 'square' }));
    const res = await quickPushSquareProduct(p);
    setRowAction(prev => { const n = { ...prev }; delete n[p.product_id]; return n; });
    setRowStatus(prev => ({ ...prev, [p.product_id]: res.ok ? { ok: true } : { ok: false, reason: res.reason } }));
    if (!res.ok) setError(res.reason);
  };

  const handleDeleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.product_name}" from this batch? This cannot be undone.`)) return;
    setError('');
    setRowAction(prev => ({ ...prev, [p.product_id]: 'delete' }));
    try {
      await apiCall(`/api/products/${p.product_id}`, { method: 'DELETE' });
      // Remove from local state immediately so the row disappears without a full re-fetch.
      setProducts(prev => prev.filter(x => x.product_id !== p.product_id));
      setRowStatus(prev => { const n = { ...prev }; delete n[p.product_id]; return n; });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Delete failed');
      setRowStatus(prev => ({ ...prev, [p.product_id]: { ok: false, reason: 'Delete failed' } }));
    } finally {
      setRowAction(prev => { const n = { ...prev }; delete n[p.product_id]; return n; });
    }
  };

  const totalQty = products.reduce((s, p) => s + (p.quantity || 0), 0);
  const totalValue = products.reduce((s, p) => s + (p.total_selling_price || 0), 0);
  // Retail (MSRP) total — retail_price is a free-text string like "$19.99" or "499"
  const parseMoney = (v) => parseFloat(String(v ?? '').replace(/[^0-9.]/g, '')) || 0;
  const totalRetail = products.reduce((s, p) => s + parseMoney(p.retail_price) * (p.quantity || 0), 0);
  const batchCost = Number(batch?.cost_of_batch || 0);
  const resaleProfit = totalValue - batchCost;

  return (
    <div className="space-y-6" data-testid="batch-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/batch-management')}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#0F172A] transition-colors"
            title="Back to Batches"
            data-testid="batch-detail-back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-[#0F172A]" data-testid="batch-detail-title">
              {batch ? batch.batch_name : 'Loading…'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">{batchId}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              ref={searchInputRef}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => {
                // USB barcode scanners end with Enter — trigger search immediately
                if (e.key === 'Enter') { e.preventDefault(); setSearch(searchInput.trim()); }
              }}
              className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none w-72"
              placeholder="Search or scan barcode…"
              data-testid="batch-detail-search"
            />
          </div>
          <BarcodeScannerButton
            onDetected={(code) => { setSearchInput(code); setSearch(code); }}
            title="Scan to find in batch"
            iconOnly
            className="p-2 rounded-lg bg-[#0052FF] hover:bg-[#0040CC] text-white transition-colors"
            testId="batch-detail-scan-btn"
          />
          {labelTemplates.length > 0 && (
            <select
              value={printTemplateId}
              onChange={e => setPrintTemplateId(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#0052FF]"
              data-testid="batch-detail-template"
            >
              {labelTemplates.map(t => <option key={t.id} value={t.id}>{t.size || t.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">SKUs</p>
          <p className="text-lg font-bold text-[#0F172A] mt-0.5">{products.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Units</p>
          <p className="text-lg font-bold text-[#0F172A] mt-0.5">{totalQty}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Resale Value</p>
          <p className="text-lg font-bold text-[#0F172A] mt-0.5">${totalValue.toFixed(2)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Batch Cost</p>
          <p className="text-lg font-bold text-[#0F172A] mt-0.5">${Number(batch?.cost_of_batch || 0).toFixed(2)}</p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="batch-detail-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Product list */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" />
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="batch-detail-empty">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No products in this batch</p>
          <p className="text-sm text-slate-400 mt-1">{search ? 'Try a different search or scan again' : 'Add products to this batch from the Product Form'}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="batch-detail-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Product</th>
                  <th className="px-4 py-3 text-left">Barcode</th>
                  <th className="px-4 py-3 text-left w-16">Qty</th>
                  <th className="px-4 py-3 text-left w-20">Price</th>
                  <th className="px-4 py-3 text-left w-24">Total</th>
                  <th className="px-4 py-3 text-left w-48">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.product_id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`batch-detail-row-${p.product_id}`}>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate('/product-form', { state: { prefillProduct: p } })}
                        className="text-left group"
                        data-testid={`batch-detail-edit-${p.product_id}`}
                      >
                        <p className="text-sm font-medium text-[#0F172A] group-hover:text-[#0052FF] group-hover:underline">{p.product_name}</p>
                        <p className="text-[10px] text-slate-400 font-mono">{p.product_id}</p>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{p.barcode || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{p.quantity || 0}</td>
                    <td className="px-4 py-3 text-slate-600">${Number(p.selling_price || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 font-semibold text-[#0F172A]">${Number(p.total_selling_price || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleQuickPrint(p)}
                          disabled={!!rowAction[p.product_id]}
                          className="p-1.5 rounded-md bg-slate-100 hover:bg-[#0052FF] hover:text-white text-slate-600 disabled:opacity-50 transition-colors"
                          title="Quick Print"
                          data-testid={`batch-detail-print-${p.product_id}`}
                        >
                          {rowAction[p.product_id] === 'print' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => handleQuickPushSquare(p)}
                          disabled={!!rowAction[p.product_id]}
                          className="p-1.5 rounded-md bg-slate-100 hover:bg-black hover:text-white text-slate-600 disabled:opacity-50 transition-colors"
                          title="Push to Square"
                          data-testid={`batch-detail-square-${p.product_id}`}
                        >
                          {rowAction[p.product_id] === 'square' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Store className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => handleQuickPushShopify(p)}
                          disabled={!!rowAction[p.product_id]}
                          className="p-1.5 rounded-md bg-slate-100 hover:bg-[#95BF47] hover:text-white text-slate-600 disabled:opacity-50 transition-colors"
                          title="Push to Shopify"
                          data-testid={`batch-detail-shopify-${p.product_id}`}
                        >
                          {rowAction[p.product_id] === 'shopify' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingBag className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(p)}
                          disabled={rowAction[p.product_id] === 'delete'}
                          className="p-1.5 rounded-md bg-slate-100 hover:bg-red-600 hover:text-white text-slate-600 disabled:opacity-50 transition-colors"
                          title="Delete from this batch"
                          data-testid={`batch-detail-delete-${p.product_id}`}
                        >
                          {rowAction[p.product_id] === 'delete' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                        {rowStatus[p.product_id]?.ok === true && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800 border border-green-300 font-semibold inline-flex items-center gap-0.5"
                            data-testid={`batch-detail-status-ok-${p.product_id}`}>
                            <CheckCircle2 className="w-3 h-3" /> done
                          </span>
                        )}
                        {rowStatus[p.product_id]?.ok === false && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-300 font-semibold inline-flex items-center gap-0.5 max-w-[140px] truncate"
                            title={rowStatus[p.product_id].reason}
                            data-testid={`batch-detail-status-fail-${p.product_id}`}>
                            <AlertCircle className="w-3 h-3 flex-shrink-0" /> {rowStatus[p.product_id].reason}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Bottom summary — both retail & resale value side by side vs cost */}
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="batch-detail-summary">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Retail Value</p>
              <p className="text-base font-bold text-slate-700" data-testid="batch-detail-total-retail">${totalRetail.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Resale Value</p>
              <p className="text-base font-bold text-[#0052FF]" data-testid="batch-detail-total-resale">${totalValue.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Cost Paid</p>
              <p className="text-base font-bold text-slate-700">${batchCost.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Profit (resale − cost)</p>
              <p className={`text-base font-bold ${resaleProfit > 0 ? 'text-green-600' : resaleProfit < 0 ? 'text-red-600' : 'text-slate-500'}`} data-testid="batch-detail-profit">
                {resaleProfit >= 0 ? '+' : ''}${resaleProfit.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Label preview modal */}
      {labelPreview && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLabelPreview(null)} data-testid="batch-detail-label-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#0052FF]">Label ready</p>
                <p className="text-sm font-semibold text-[#0F172A] truncate max-w-xs">{labelPreview.product_name}</p>
              </div>
              <button onClick={() => setLabelPreview(null)} className="text-slate-400 hover:text-slate-600 p-1" data-testid="batch-detail-label-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 bg-slate-50 flex items-center justify-center">
              <img src={labelPreview.image} alt="label" className="max-w-full max-h-[50vh] border border-slate-200 bg-white shadow-sm" />
            </div>
            <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-200 bg-white">
              <button
                onClick={() => openSingleLabelPrint(labelPreview.image, `Label · ${labelPreview.product_name}`, labelPreview.size)}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors"
                data-testid="batch-detail-label-print"
              >
                <Printer className="w-4 h-4" /> Print
              </button>
              <button onClick={() => setLabelPreview(null)}
                className="px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium"
                data-testid="batch-detail-label-cancel"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
